import {
  ApartmentOutlined,
  AuditOutlined,
  BankOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  CarOutlined,
  CopyrightOutlined,
  DashboardOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  FileProtectOutlined,
  GiftOutlined,
  GoldOutlined,
  MedicineBoxOutlined,
  GlobalOutlined,
  RocketOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ComponentType } from 'react';

import type { Permission } from '@/auth/permissions';

export type NavItem = {
  key: string;
  path: string;
  label: string;
  icon?: ComponentType;
  /** Hidden unless the signed-in user holds at least one of these. */
  permissions?: Permission[];
  /** Needs a pharmacy chosen in the scope picker before it can load anything. */
  pharmacyScoped?: boolean;
  /** Backend answers 501 today; shown greyed so the shape is discoverable. */
  stubbed?: boolean;
};

/**
 * A section that folds. Its own children are hidden until it is opened, which
 * is the whole point — see the note on NAVIGATION below.
 */
export type NavGroup = {
  key: string;
  label: string;
  icon: ComponentType;
  items: NavItem[];
};

export type NavEntry = NavItem | NavGroup;

export function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

/**
 * The sidebar, grouped by the thing you are working ON rather than by the part
 * of the system a screen happens to live in.
 *
 * Two rules shape this list.
 *
 * **Only what you reach for constantly stays flat.** Eighteen links at one level
 * is not navigation, it is an index — you read all of it every time because
 * nothing tells you which part matters now. The four that sit at the top are the
 * ones an operator opens daily; everything else is one click behind a section
 * that opens itself when you are inside it.
 *
 * **Things that are about the same subject sit together**, which several were
 * not. Listings, Stock, Supply rates and Settlements are every one of them a
 * fact about ONE partner shop, and they were spread across two groups called
 * "Commerce" and "Platform" — so answering "how is Sen Medical doing" meant
 * hopping the list. They are now one section, in the order you would ask the
 * questions: who they are, what we sell of theirs, what they hold, what we pay
 * them, what we owe them.
 *
 * The split between Supply rates (here) and Retail pricing (under Customer
 * pricing) is deliberate and is the money model showing through: one is a
 * contract with a shop, the other is our own decision about what to charge, and
 * filing them together is exactly the conflation the reseller rewrite removed.
 */
export const NAVIGATION: NavEntry[] = [
  { key: 'dashboard', path: '/', label: 'Dashboard', icon: DashboardOutlined },
  {
    key: 'orders',
    path: '/orders',
    label: 'Orders',
    icon: ShoppingCartOutlined,
    permissions: ['order:read:any'],
  },
  {
    key: 'prescriptions',
    path: '/prescriptions',
    label: 'Prescriptions',
    icon: FileProtectOutlined,
    permissions: ['prescription:read:any'],
  },

  {
    key: 'catalog',
    label: 'Catalog',
    icon: MedicineBoxOutlined,
    items: [
      {
        key: 'medicines',
        path: '/catalog/medicines',
        label: 'Medicines',
        icon: MedicineBoxOutlined,
        permissions: ['catalog:read'],
      },
      {
        key: 'brands',
        path: '/catalog/brands',
        label: 'Brands',
        icon: CopyrightOutlined,
        permissions: ['catalog:read'],
      },
      {
        key: 'categories',
        path: '/catalog/categories',
        label: 'Categories',
        icon: ApartmentOutlined,
        permissions: ['catalog:read'],
      },
      {
        key: 'manufacturers',
        path: '/catalog/manufacturers',
        label: 'Manufacturers',
        icon: ExperimentOutlined,
        permissions: ['catalog:read'],
      },
      {
        key: 'recalls',
        path: '/catalog/recalls',
        label: 'Recalls',
        icon: WarningOutlined,
        permissions: ['catalog:read'],
      },
    ],
  },

  {
    key: 'pharmacies',
    label: 'Pharmacies',
    icon: ShopOutlined,
    items: [
      {
        key: 'pharmacy-list',
        path: '/pharmacies',
        label: 'All pharmacies',
        icon: ShopOutlined,
        permissions: ['pharmacy:read'],
      },
      {
        key: 'listings',
        path: '/listings',
        label: 'Listings',
        icon: TagsOutlined,
        permissions: ['listing:read'],
        pharmacyScoped: true,
      },
      {
        key: 'stock',
        path: '/inventory',
        label: 'Stock',
        icon: GoldOutlined,
        permissions: ['inventory:read:any'],
      },
      {
        key: 'supply-rules',
        path: '/supply-rules',
        label: 'Supply rates',
        icon: DollarOutlined,
        permissions: ['settlement:write'],
        pharmacyScoped: true,
      },
      {
        key: 'settlements',
        path: '/settlements',
        label: 'Settlements',
        icon: BankOutlined,
        permissions: ['settlement:read'],
      },
    ],
  },

  {
    key: 'pricing',
    label: 'Customer pricing',
    icon: DollarOutlined,
    items: [
      {
        key: 'retail-rules',
        path: '/retail-rules',
        label: 'Retail pricing',
        icon: DollarOutlined,
        permissions: ['settlement:read:any'],
      },
      {
        key: 'coupons',
        path: '/coupons',
        label: 'Coupons',
        icon: GiftOutlined,
        permissions: ['coupon:read'],
      },
    ],
  },

  {
    key: 'delivery',
    label: 'Delivery',
    icon: CarOutlined,
    items: [
      {
        key: 'deliveries',
        path: '/deliveries',
        label: 'Deliveries',
        icon: CarOutlined,
        permissions: ['delivery:read'],
      },
      {
        key: 'riders',
        path: '/riders',
        label: 'Riders',
        icon: RocketOutlined,
        permissions: ['delivery:assign'],
      },
      {
        key: 'fees',
        path: '/settings/fees',
        label: 'Fares & fees',
        icon: EnvironmentOutlined,
        permissions: ['config:read'],
      },
    ],
  },
];

/**
 * Pinned to the bottom, away from the work, and folded.
 *
 * Administering the tool is not the job the tool is for. These four are opened
 * when something has gone wrong or somebody has joined — rarely, and never in
 * the middle of an order — so they sit at the end where they are findable
 * without spending a line of the reading order every day.
 *
 * They used to sit there as four open rows, which gave them exactly the weight
 * of Orders and Prescriptions and made the bottom of the sider as busy as the
 * top. Folded into one section they cost a single row, and the label on that
 * row — Settings — is the word somebody looks for when they want any of the
 * four. Same placement, a quarter of the footprint.
 *
 * It stays a list of its own rather than joining NAVIGATION, because the point
 * of pinning it is that it does not scroll away with the work above it.
 */
export const NAVIGATION_FOOTER: NavEntry[] = [
  {
    key: 'settings',
    label: 'Settings',
    icon: SettingOutlined,
    items: [
      {
        key: 'users',
        path: '/users',
        label: 'Users & roles',
        icon: TeamOutlined,
        permissions: ['user:read'],
      },
      /*
       * SUPER_ADMIN only, like the two below it.
       *
       * The trail records what every administrator did with their powers, so
       * the people who can read it are deliberately fewer than the people it
       * describes. An ADMIN does not see this row at all — the same treatment
       * the messaging and security screens get, and for the same reason: a link
       * that always answers 403 reads as a broken panel, not as a boundary.
       */
      {
        key: 'audit',
        path: '/audit',
        label: 'Audit log',
        icon: AuditOutlined,
        permissions: ['audit:read'],
      },
      /*
       * SUPER_ADMIN only as well, and hidden outright from everyone else.
       *
       * `config:write` is held by no other role, so for an ADMIN these two
       * simply do not exist in the sidebar. With the audit log above them now
       * restricted too, an ADMIN sees Settings holding two rows — Users & roles
       * and Regional — and the section itself disappears for anyone holding
       * none of them.
       */
      {
        key: 'messaging',
        path: '/settings/messaging',
        label: 'Messaging & OTP',
        icon: MessageOutlined,
        permissions: ['config:write'],
      },
      {
        key: 'security',
        path: '/settings/security',
        label: 'Security limits',
        icon: SafetyCertificateOutlined,
        permissions: ['config:write'],
      },
      /*
       * `config:read`, so every ADMIN sees it — unlike the three above. The
       * clock the panel renders every date on is not a secret, and an operator
       * who cannot check it cannot trust a timestamp.
       */
      {
        key: 'regional',
        path: '/settings/regional',
        label: 'Regional',
        icon: GlobalOutlined,
        permissions: ['config:read'],
      },
    ],
  },
];

/** Every destination, flattened — used to resolve the active route. */
export const ALL_NAV_ITEMS: NavItem[] = [...NAVIGATION, ...NAVIGATION_FOOTER].flatMap((entry) =>
  isGroup(entry) ? entry.items : [entry],
);

/**
 * The section a path lives in, so it can be opened when you land inside it.
 *
 * Both lists, not just NAVIGATION: /users is inside a folding section now, and
 * searching only the main list would land you on Users & roles with Settings
 * still shut and nothing in the sidebar marking where you are.
 */
export function groupKeyForPath(path: string): string | undefined {
  return [...NAVIGATION, ...NAVIGATION_FOOTER].find(
    (entry) => isGroup(entry) && entry.items.some((item) => item.path === path),
  )?.key;
}
